import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import RoleGuard from "../auth/RoleGuard";
import { MODULES, ROLE_GROUPS } from "../auth/roles";
import AccessDenied from "./ui/AccessDenied";

/*Import Context */
import { UserContext } from "./UserContext";

/*Import API helpers */
import { fetchGET } from "../apiHelper";
import {
  podatkiZaRazpis,
  potDoPrograma,
  financniViriRazpisa,
  projektiZaRazpis,
} from "../listOfApi";

/* React-Bootstrap component */
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";

/*import React-icons */
import { AiOutlineInfoCircle } from "react-icons/ai";
import { BsGrid3X3GapFill } from "react-icons/bs";
import { BsFileText } from "react-icons/bs";
import { MdDescription } from "react-icons/md";

/*Import Components */
import FinancniViri from "./tenderDetails/FinancniViri";
import OsnovniPodatki from "./tenderDetails/OsnovniPodatki";

const TenderDetails = () => {
  /*Pridobljen parameter iz ID */
  const { id } = useParams();
  const navigate = useNavigate();

  const [tenderDataDetails, setTenderDataDetails] = useState({
    razpis_naziv: "",
    obtezitev: 0,
    opis: "",
    povezava: "",
    pomemben: false,
    odprt: false,
    odprt_tekst: "",
    prijava_datum: "",
    prijava_ura: "",
    sifrantARRS: false,
    sifrantCERIF: false,
    sifrantKLASIUS: false,
    program_naziv: "",
    id_financiranje_kategorija: 0,
    financiranje_kategorija: "",
    program: [],
  });
  const [financialResources, setFinancialResources] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invalidRazpisId, setInvalidRazpisId] = useState(false);

  /* Pridobivanje podatkov za razpis in finančne vire razpisa */
  useEffect(() => {
    getRazpis();
    getRazpisData();
    getProjects();
  }, []);

  /* Pridobi podatke za razpis */
  const getRazpis = () => {
    fetchGET(`${podatkiZaRazpis}/${id}`).then((razpis) => {
      if (razpis !== 0) {
        setTenderDataDetails({
          razpis_naziv: razpis.razpis_naziv,
          povezava: razpis.povezava,
          pomemben: razpis.pomemben,
          obtezitev: parseInt(razpis.obtezitev),
          odprt: razpis.odprt,
          odprt_tekst: razpis.odprt_tekst,
          opis: razpis.opis,
          prijava_datum: razpis.prijava_datum,
          prijava_ura: razpis.prijava_ura,
          sifrantARRS: razpis.sifrantARRS,
          sifrantCERIF: razpis.sifrantCERIF,
          sifrantKLASIUS: razpis.sifrantKLASIUS,
          program_naziv: razpis.program_naziv,
          programID: razpis.programID,
          financiranje_kategorija: razpis.financiranje_kategorija,
          id_financiranje_kategorija: razpis.id_financiranje_kategorija,
        });

        fetchGET(`${potDoPrograma}/${razpis.programID}`).then((poti) => {
          setTenderDataDetails((prevState) => {
            return { ...prevState, program: poti };
          });
        });
      } else {
        setInvalidRazpisId(true);
      }
    });
  };

  /* Pridobi podatke za finančne vire razpisa */
  const getRazpisData = () => {
    fetchGET(`${financniViriRazpisa}/${id}/financiranje`).then((vir) => {
      if (vir !== 0) {
        setFinancialResources(vir);
      } else {
        setFinancialResources([]);
      }
    });
  };

  /* Pridobi podatke za projekte razpisa */
  const getProjects = () => {
    fetchGET(`${projektiZaRazpis}/${id}/projekti`)
      .then((projekti) => {
        if (projekti !== 0) {
          setProjects(projekti);
        } else {
          setProjects([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
      });
  };

  return (
    <RoleGuard
      moduleKey={MODULES.RAZPISI}
      roles={ROLE_GROUPS.GLOBALNI_AND_FAKULTETNI}
      fallback={<AccessDenied />}
    >
      <Container fluid>
        {invalidRazpisId && <p>Razpis ne obstaja!</p>}
        {!invalidRazpisId && (
          <>
            {/* PROJEKT TITLE */}
            <Row>
              <Col className="main-header font-weight-bold">
                <AiOutlineInfoCircle className="me-2" />
                {tenderDataDetails.razpis_naziv}
              </Col>
              <Col>
                <div className="float-end">
                  <Link
                    className="programs-button-add-text ms-2"
                    to={`/razpisi`}
                  >
                    <Button variant="primary" className="button-back-razpisi ">
                      <BsGrid3X3GapFill /> <span>Razpisi</span>
                    </Button>
                  </Link>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className="header-divider">
                <hr />
              </Col>
            </Row>
            <Row>
              <Col>
                {/* TABS */}
                <Tabs defaultActiveKey="osnovni">
                  {/* TAB - Osnovni podatki */}
                  <Tab eventKey="osnovni" title="Osnovni podatki">
                    <OsnovniPodatki
                      id={id}
                      tenderDataDetails={tenderDataDetails}
                      getRazpis={getRazpis}
                      getRazpisData={getRazpisData}
                    />
                  </Tab>
                  {/* TAB - Finančni podatki */}
                  <Tab eventKey="financniVir" title="Finančni viri">
                    <FinancniViri
                      id={id}
                      financialResources={financialResources}
                      financiranjeKategorija={
                        tenderDataDetails.id_financiranje_kategorija
                      }
                      getRazpisData={getRazpisData}
                    />
                  </Tab>
                  {/* TAB - Projekti */}
                  <Tab eventKey="projekti" title="Projekti">
                    <Card className="razpis-card mt-3">
                      <Card.Body>
                        {projects.length > 0 ? (
                          <div className="table-responsive">
                            <Table hover>
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      fontWeight: "500",
                                      color: "#333",
                                      background: "rgb(249, 249, 249)",
                                      borderBottom: "2px solid #e0e0e0",
                                      padding: "12px 8px",
                                    }}
                                  >
                                    Naslov projekta
                                  </th>
                                  <th
                                    style={{
                                      fontWeight: "500",
                                      color: "#333",
                                      background: "rgb(249, 249, 249)",
                                      borderBottom: "2px solid #e0e0e0",
                                      padding: "12px 8px",
                                    }}
                                  >
                                    Vloga UM v projektu
                                  </th>
                                  <th
                                    style={{
                                      fontWeight: "500",
                                      color: "#333",
                                      background: "rgb(249, 249, 249)",
                                      borderBottom: "2px solid #e0e0e0",
                                      padding: "12px 8px",
                                    }}
                                  >
                                    Status
                                  </th>
                                  <th
                                    style={{
                                      fontWeight: "500",
                                      color: "#333",
                                      background: "rgb(249, 249, 249)",
                                      borderBottom: "2px solid #e0e0e0",
                                      padding: "12px 8px",
                                    }}
                                  >
                                    Občanska znanost
                                  </th>
                                  <th
                                    style={{
                                      fontWeight: "500",
                                      color: "#333",
                                      background: "rgb(249, 249, 249)",
                                      borderBottom: "2px solid #e0e0e0",
                                      padding: "12px 8px",
                                    }}
                                  >
                                    Začetek projekta
                                  </th>
                                  <th
                                    style={{
                                      fontWeight: "500",
                                      color: "#333",
                                      background: "rgb(249, 249, 249)",
                                      borderBottom: "2px solid #e0e0e0",
                                      padding: "12px 8px",
                                    }}
                                  >
                                    Konec projekta
                                  </th>
                                  <th
                                    className="text-center"
                                    style={{
                                      fontWeight: "500",
                                      color: "#333",
                                      background: "rgb(249, 249, 249)",
                                      borderBottom: "2px solid #e0e0e0",
                                      padding: "12px 8px",
                                    }}
                                  >
                                    Podrobnosti
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {projects.map((project, index) => (
                                  <tr key={index}>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #e0e0e0",
                                        padding: "12px 8px",
                                      }}
                                    >
                                      {project.naslov}
                                    </td>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #e0e0e0",
                                        padding: "12px 8px",
                                      }}
                                    >
                                      {project.vloga_um}
                                    </td>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #e0e0e0",
                                        padding: "12px 8px",
                                      }}
                                    >
                                      {project.status}
                                    </td>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #e0e0e0",
                                        padding: "12px 8px",
                                      }}
                                    >
                                      {project.obcanska_znanost ? "Da" : "Ne"}
                                    </td>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #e0e0e0",
                                        padding: "12px 8px",
                                      }}
                                    >
                                      {project.zacetek
                                        ? new Date(
                                            project.zacetek,
                                          ).toLocaleDateString("sl-SI")
                                        : "-"}
                                    </td>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #e0e0e0",
                                        padding: "12px 8px",
                                      }}
                                    >
                                      {project.konec
                                        ? new Date(
                                            project.konec,
                                          ).toLocaleDateString("sl-SI")
                                        : "-"}
                                    </td>
                                    <td
                                      className="text-center"
                                      style={{
                                        borderBottom: "1px solid #e0e0e0",
                                        padding: "12px 8px",
                                      }}
                                    >
                                      <Button
                                        variant="outline-primary"
                                        className="border-0"
                                        size="sm"
                                        onClick={() =>
                                          window.open(
                                            project.link_podrobnosti,
                                            "_blank",
                                          )
                                        }
                                        title="Podrobnosti projekta"
                                      >
                                        <MdDescription size={18} />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <p className="mb-0">Ni projektov za ta razpis</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab>
                </Tabs>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </RoleGuard>
  );
};

export default TenderDetails;
